import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { navigate } from 'gatsby';
import { Button, Container, Form, Grid, Input, Segment, Tab, Table, } from 'semantic-ui-react';
import { fetchOrder, setOrderStatus } from '../../actions/support';
import FilePane from './FilePane';
import CompanyPanel from './CompanyPanel';

const getStatusIcon = status => {
  switch (status) {
    case 'rejected':
      return 'times';

    case 'accepted':
      return 'check';

    default:
      return "";
  }
};

const mapStateToProps = ({ support }) => ({
  order: support.order,
  loading: support.loading,
});

const getMenuItem = (file, type) => {
  const statusIcon = getStatusIcon(file && file.status);
  const typeName = type.replaceAll("_", " ");
  return statusIcon
    ? {
        key: type,
        icon: statusIcon,
        content: typeName,
      }
    : typeName;
};

const DescriptionTable = ({ order }) => (
  <Table compact celled>
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell
          colspan={6}
          textAlign="center"
        >{`ORDER #${order.code}`}</Table.HeaderCell>
      </Table.Row>
      <Table.Row>
        <Table.HeaderCell>Code</Table.HeaderCell>
        <Table.HeaderCell>Status</Table.HeaderCell>
        <Table.HeaderCell>Type</Table.HeaderCell>
        <Table.HeaderCell>Verification method</Table.HeaderCell>
        <Table.HeaderCell>Verification status</Table.HeaderCell>
        <Table.HeaderCell>Updated at</Table.HeaderCell>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      <Table.Row>
        <Table.Cell>{order.code}</Table.Cell>
        <Table.Cell>
          {order.status.replaceAll("_", " ").toUpperCase()}
        </Table.Cell>
        <Table.Cell>{order.type.replaceAll("_", " ").toUpperCase()}</Table.Cell>
        <Table.Cell>
          {(order.identityVerificationMethod || "")
            .replaceAll("_", " ")
            .toUpperCase()}
        </Table.Cell>
        <Table.Cell>
          {(order.identityVerificationStatus || "")
            .replaceAll("_", " ")
            .toUpperCase()}
        </Table.Cell>
        <Table.Cell>{order.updatedAt}</Table.Cell>
      </Table.Row>
    </Table.Body>
  </Table>
);

const ContractorTable = ({ order }) => {
  if (!order.contractor) {
    return <Segment>Verification transfer data not available yet</Segment>;
  }

  return (
    <Table>
      <Table.Header>
        <Table.HeaderCell colSpan={2} textAlign="center">
          VERIFICATION TRANSFER DATA
        </Table.HeaderCell>
      </Table.Header>
      <Table.Body>
        <Table.Row>
          <Table.Cell width={8}>CONTRACTOR IBAN</Table.Cell>
          <Table.Cell>{order.contractor.iban}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>CONTRACTOR NAME</Table.Cell>
          <Table.Cell>{order.contractor.name}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
  );
};

const SupportOrderDetailsPanel = ({ order, code, fetchOrder, setOrderStatus }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const handleAccept = () => setOrderStatus(order.code, "accepted");
  const handleReject = () =>
    setOrderStatus(order.code, "rejected", rejectionReason);
  const handleChange = (e, { value }) => setRejectionReason(value);

  useEffect(() => {
    setLoading(true);
    fetchOrder(code);
  }, [code]);

  useEffect(() => {
    if (order?.code === code) setLoading(false);
  }, [order, code]);

  if (loading) {
    return <Segment basic loading style={{ height: "80vh" }} />;
  }

  const panes = Object.entries(order.files || {}).map(([type, file]) => ({
    menuItem: getMenuItem(file, type),
    render: () => <FilePane file={file} />,
  }));

  return (
    <Container>
      <Grid centered>
        {order.parentOrderCode && (
          <Grid.Row>
            <Button
              basic
              primary
              onClick={() =>
                navigate(`/dashboard/verification/${order.parentOrderCode}`)
              }
              content="SHOW COMPANY ORDER"
            />
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column>
            <DescriptionTable order={order} />
          </Grid.Column>
        </Grid.Row>
        {order.identityVerificationMethod === "fiberpay_transfer" && (
          <Grid.Row>
            <Grid.Column>
              <ContractorTable order={order} />
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column width={!!panes.length ? 4 : 16}>
            <Table>
              <Table.Header>
                <Table.HeaderCell colspan={2} textAlign="center">
                  FORM DATA
                </Table.HeaderCell>
              </Table.Header>
              <Table.Body>
                {Object.values(order.formAnswers)
                  .map(
                    ({
                      question,
                      answer
                    }) =>
                      answer !== null &&
                      (Array.isArray(answer) ? (
                        <>
                          {answer.map((a) => (
                            <Table.Row key={a}>
                              <Table.Cell>{question.content_pl}</Table.Cell>
                              <Table.Cell>{`${a.toUpperCase()}`}</Table.Cell>
                            </Table.Row>
                          ))}
                        </>
                      ) : (
                        <Table.Row key={question.content_pl}>
                          <Table.Cell>{question.content_pl}</Table.Cell>
                          <Table.Cell>{`${(
                            '' + answer
                          ).toUpperCase()}`}</Table.Cell>
                        </Table.Row>
                      ))
                  )}
              </Table.Body>
            </Table>
          </Grid.Column>
          {order.type === "company" ? (
            <Grid.Column width={!!panes.length ? 12 : 16}>
              <CompanyPanel order={order} />
            </Grid.Column>
          ) : (
            <Grid.Column width={12}>
              {!!panes.length && <Tab panes={panes} />}
              <Grid.Row>
                <Segment
                  basic
                  className="flexbox-spaced-between"
                  style={{ padding: "1rem 0", width: "100%" }}
                  padded
                >
                  <Form onSubmit={handleReject}>
                    <Input
                      required
                      type="text"
                      placeholder="Rejection reason"
                      style={{ width: "320px" }}
                      value={rejectionReason}
                      onChange={handleChange}
                    />
                    <Button
                      type="submit"
                      color="red"
                      style={{ margin: "0 1rem" }}
                      disabled={!rejectionReason}
                    >
                      REJECT
                    </Button>
                  </Form>
                  <Button positive onClick={handleAccept}>
                    ACCEPT
                  </Button>
                </Segment>
              </Grid.Row>
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
    </Container>
  );
};

const mapDispatchToProps = { fetchOrder, setOrderStatus };

export default connect(mapStateToProps, mapDispatchToProps)(SupportOrderDetailsPanel);
